// ui/src/components/shared/ApprovalEligibilityGate.jsx

/**
 * @file ApprovalEligibilityGate.jsx
 * @description Presentation-only gate component. Disables children and renders
 *   a contextual blocked message when callerEligibility.eligible is false.
 *   Consumes eligibility shape from ep 9 (plan approve), ep 14 (amendment approve),
 *   ep 22 (plan approval-review), ep 27 (change-set eligible check).
 *   No API calls — prop-driven. SoD enforcement is server-side only.
 * @module shared
 * @author data-fit LLC · DecisionFlow · S775
 * @see api_spec_fileA_v4_final.md §3.1–§3.7 — callerEligibility shape
 */

import React, {
  Children,
  cloneElement,
  isValidElement,
  useEffect,
  useRef,
} from 'react';

const DEFAULT_MESSAGES = {
  self_submitted:
    'You submitted this item. Another approver in your scope is required.',

  out_of_scope:
    "This plan's assignment scope falls outside your approval authority.",

  no_eligible_approver:
    'No eligible approver is currently available for this plan\'s scope. Contact your compensation administrator.',

  already_approved:
    'This item has already been approved.',

  wrong_status:
    'This item is not currently in a state that can be approved.',

  scope_mismatch:
    "Your approval scope does not fully cover this plan's assignment scope.",

  unknown:
    'Approval is not available at this time.',
};

function messageClassForReason(reasonCode) {
  switch (reasonCode) {
    case 'self_submitted':
      return 'modifier-manager-info';

    case 'out_of_scope':
    case 'scope_mismatch':
      return 'modifier-manager-warning';

    case 'no_eligible_approver':
      return 'modifier-manager-inline-error';

    default:
      return 'modifier-manager-info';
  }
}

/**
 * Presentation-only helper.
 *
 * It passes `disabled` and `aria-disabled` to immediate valid React children.
 * Consuming custom button components must forward these props to their
 * underlying interactive element.
 *
 * @param {React.ReactNode} children
 * @returns {React.ReactNode}
 */
function renderDisabledChildren(children) {
  return Children.map(children, (child) => {
    if (!isValidElement(child)) return child;

    return cloneElement(child, {
      disabled: true,
      'aria-disabled': true,
      onClick: undefined,
    });
  });
}

/**
 * @param {{
 *   eligibility: {
 *     canApprove: boolean,
 *     reasonCode:
 *       'eligible'|'self_submitted'|'out_of_scope'|
 *       'no_eligible_approver'|'already_approved'|
 *       'wrong_status'|'scope_mismatch'|null,
 *     requiresOtherApprover: boolean,
 *     scopeCoverage?: 'full'|'partial'|'none'
 *   },
 *   children: React.ReactNode,
 *   blockedMessage?: string,
 *   showAlways?: boolean,
 *   className?: string
 * }} props
 */
export default function ApprovalEligibilityGate({
  eligibility,
  children,
  blockedMessage,
  showAlways = false,
  className = '',
}) {
  const {
    canApprove = false,
    reasonCode = null,
  } = eligibility || {};

  const previousReasonRef = useRef(reasonCode);
  const reasonChanged = previousReasonRef.current !== reasonCode;

  useEffect(() => {
    previousReasonRef.current = reasonCode;
  }, [reasonCode]);

  if (canApprove) {
    return <>{children}</>;
  }

  const message =
    blockedMessage ||
    DEFAULT_MESSAGES[reasonCode] ||
    DEFAULT_MESSAGES.unknown;

  const messageClass = messageClassForReason(reasonCode);

  return (
    <div className={className}>
      {showAlways && (
        <div
          className="flex flex-wrap items-center gap-2"
          aria-disabled="true"
        >
          {renderDisabledChildren(children)}
        </div>
      )}

      <div
        className={`${messageClass} ${showAlways ? 'mt-2' : ''}`}
        role={reasonChanged ? 'alert' : 'status'}
        aria-live={reasonChanged ? 'assertive' : 'polite'}
      >
        {message}
      </div>
    </div>
  );
}
